import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Clock, Info, Calculator, ShieldCheck } from 'lucide-react';

/**
 * TimeSavingsModal - Transparent breakdown of time savings calculation
 * 
 * Shows the user exactly how the "time saved" estimate was calculated,
 * including per-rule breakdown, expert time costs, and the conservative factor.
 */
const TimeSavingsModal = ({ 
  isOpen, 
  onClose, 
  timeSaved,
  rowCount 
}) => {
  if (!timeSaved) return null;

  const breakdown = timeSaved.breakdown_by_rule || {};
  const hasBreakdown = Object.keys(breakdown).length > 0;
  const totalRules = timeSaved.total_rules || Object.keys(breakdown).length;
  const totalWeightPerRow = timeSaved.total_weight_per_row || 0;

  // Format time display
  const formatTime = (seconds) => {
    if (!seconds || seconds <= 0) return '0 min';
    const hours = seconds / 3600;
    const minutes = seconds / 60;
    const days = hours / 8;
    
    if (days >= 1) {
      return `${Math.round(hours)} hours (${days.toFixed(1)} working days)`;
    } else if (hours >= 1) {
      return `${hours.toFixed(1)} hours`;
    } else {
      return `${Math.max(1, Math.round(minutes))} minutes`;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Calculator className="w-5 h-5 text-emerald-600" />
            How we calculate time saved
          </DialogTitle>
          <DialogDescription>
            Transparent breakdown of your estimated time savings
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Main Result */}
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center">
                <Clock className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-emerald-800">
                  ~{formatTime(timeSaved.total_seconds)}
                </p>
                <p className="text-sm text-emerald-600">
                  Estimated manual Excel time saved
                </p>
              </div>
            </div>
          </div>

          {/* Breakdown Table */}
          {hasBreakdown && (
            <div className="space-y-3">
              <h4 className="font-semibold text-slate-900 flex items-center gap-2">
                <Info className="w-4 h-4 text-blue-500" />
                Breakdown by Cleaning Task
              </h4>
              <p className="text-sm text-slate-500">
                We analyzed the time required for an Excel expert to manually fix these specific data issues:
              </p>
              
              <div className="bg-slate-50 rounded-lg border border-slate-200 overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100">
                    <tr>
                      <th className="text-left px-4 py-2 font-medium text-slate-700">Cleaning Task</th>
                      <th className="text-right px-4 py-2 font-medium text-slate-700">Expert Time</th>
                      <th className="text-right px-4 py-2 font-medium text-slate-700">Columns</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {Object.entries(breakdown).map(([ruleType, data]) => (
                      <tr key={ruleType} className="hover:bg-slate-50">
                        <td className="px-4 py-3">
                          <div>
                            <p className="font-medium text-slate-800">
                              {data.display_name || ruleType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                            </p>
                            {data.explanation && (
                              <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{data.explanation}</p>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-slate-600">
                          {data.seconds_per_row}s/row
                        </td>
                        <td className="px-4 py-3 text-right text-slate-600">
                          {data.columns?.length || 1}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Calculation Explanation */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
            <h4 className="font-semibold text-blue-900 flex items-center gap-2">
              <Calculator className="w-4 h-4" />
              Calculation Formula
            </h4>
            <div className="text-sm text-blue-800 space-y-2">
              <p>
                <span className="font-mono bg-blue-100 px-2 py-0.5 rounded">
                  Total = Rows × Σ(Rule Weights) × 0.66
                </span>
              </p>
              <p>
                <strong>{(rowCount || timeSaved.rows_processed || 0).toLocaleString()} rows</strong> × {' '}
                <strong>{totalWeightPerRow}s</strong> per row ({totalRules} rules)
              </p>
              {timeSaved.raw_total_seconds > 0 && (
                <p className="text-xs text-blue-600">
                  Raw calculation: {formatTime(timeSaved.raw_total_seconds)} → 
                  After 34% reduction: {formatTime(timeSaved.total_seconds)}
                </p>
              )}
            </div>
          </div>

          {/* Conservative Factor Disclaimer */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex gap-3">
              <ShieldCheck className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-800">
                <p className="font-medium mb-1">Conservative Estimate (34% reduction applied)</p>
                <p className="text-amber-700">
                  These estimates are based on the time required for an Excel expert to apply formulas 
                  and manually audit rows. We have applied a <strong>34% reduction</strong> (safety factor of 0.66) 
                  to provide a conservative estimate, accounting for potential use of macros or varying user speeds.
                </p>
              </div>
            </div>
          </div>

          {/* Data Transparency Note */}
          <p className="text-xs text-slate-400 text-center">
            Time estimates based on Excel expert benchmarks for formula setup + manual audit per row.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TimeSavingsModal;
